import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {Owner} from 'auth/PermissionConstants';
import {MAX_TASK_SCHEMA_NAME_LENGTH} from 'processes/settings/fields/ProcessTaskSchemaFieldConstants';
import SchemaOwnerDropdown from 'results/assay-schemas/SchemaOwnerDropdown';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import {GRAY_DARKER} from 'ui-library/Colors';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import {EntityWithTextAndSuspended, MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import ErrorMessage from 'ui-library/ErrorMessage';
import SettingsField from 'ui-library/SettingsField';

interface TextFieldProps {
  label: string;
  value: string;
  isBeingCreated: boolean;
  onChange: (x: string) => void;
  error: string;
  dataTestAttrs?: DataTestAttrs;
}

interface OwnerFieldProps {
  className?: string;
  isEditable: boolean;
  ownerChoices: Array<Owner>;
  owner: Owner | null;
  onOwnerChanged: (owner: Owner) => void;
  isRequired?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

interface PrefixPreviewProps {
  value: string | null;
}

type PrefixFieldProps = TextFieldProps & {prefixPreview?: string; isDisabled?: boolean; isRequired?: boolean};
type NameFieldProps = TextFieldProps & {isDisabled?: boolean; isRequired?: boolean};

interface ProcessSchemaMetadataSectionRowProps {
  children: ReactNode;
}

export function OwnerField(props: OwnerFieldProps): JSX.Element {
  return !props.isEditable ? (
    <SettingsField
      label="Owner"
      name="Owner"
      value={props.owner?.name}
      isBeingCreated={false}
      isRequired={props.isRequired}
      isWritable={false}
      onChange={() => {}}
      dataTestAttrs={props.dataTestAttrs}
    />
  ) : (
    <SchemaOwnerDropdown {...props} />
  );
}

function PrefixPreview(props: PrefixPreviewProps): JSX.Element {
  return (
    <>
      {props.value != null && (
        <Tooltip title={props.value} placement={TooltipPlacement.BOTTOM}>
          <div className={css(styles.prefixPreview)}>{props.value}</div>
        </Tooltip>
      )}
    </>
  );
}

export function PrefixField(props: PrefixFieldProps): JSX.Element {
  return (
    <div>
      <SettingsField
        label={props.label}
        name={props.label}
        error={props.error}
        value={props.value}
        isBeingCreated={props.isBeingCreated}
        isRequired={props.isRequired ?? true}
        isWritable={Boolean(!props.isDisabled)}
        onChange={(e: string) => {
          props.onChange(e);
        }}
        dataTestAttrs={props.dataTestAttrs}
      />
      {props.error === '' ? (
        <PrefixPreview value={props.prefixPreview ?? null} />
      ) : (
        <ErrorMessage error={props.error} />
      )}
    </div>
  );
}

export function NameField(props: NameFieldProps): JSX.Element {
  return (
    <>
      <SettingsField
        label={props.label}
        name={props.label}
        error={props.error}
        value={props.value}
        isBeingCreated={props.isBeingCreated}
        isRequired={props.isRequired ?? true}
        isWritable={Boolean(!props.isDisabled)}
        onChange={(e: string) => {
          props.onChange(e);
        }}
        dataTestAttrs={props.dataTestAttrs}
        maxLength={MAX_TASK_SCHEMA_NAME_LENGTH}
      />
      <ErrorMessage error={props.error} />
    </>
  );
}

export default function ProcessSchemaMetadataSectionRow(
  props: ProcessSchemaMetadataSectionRowProps
): JSX.Element {
  return (
    <div className={css(styles.schemaMetadataSectionRow)}>
      {React.Children.map(props.children, (child) => (
        <div className={css(styles.schemaMetadataColumn)}>
          <div className={classNames(css(styles.schemaMetadataField), 'u-base-text')}>{child}</div>
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  schemaMetadataSectionRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: '24px',
  },
  schemaMetadataColumn: {
    flex: 0.33,
    minWidth: 0,
  },
  schemaMetadataField: {
    width: '80%',
    marginBottom: '0',
  },
  prefixPreview: {
    height: '18px',
    fontSize: '12px',
    marginBottom: '2px',
    verticalAlign: 'top',
    color: GRAY_DARKER,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
});
